import { memo, useEffect, useRef, useState } from 'react';

import type { QueryClient } from '@zg-rentals/react-query5';

import { Box, Button, Heading, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useRentalManagerRenterContactWebApiHandler_getLikelyTenants } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import TenantSelectFormBody, { type TenantSelectFormBodyRef } from '../TenantSelectFormBody';

import type { Client } from '@zg-rentals/http-client';
import type { RenterInfoRenterAddedDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type TenantSelectProps = {
  client: Client;
  isOpen: boolean;
  listingAlias: string;
  modalTitle?: string;
  onAddRenterSuccess?: () => void;
  onAddRenterError?: (error: string) => void;
  onClose: () => void;
  onPrimaryCTAClick?: (addedRenters?: Array<RenterInfoRenterAddedDto>) => void | undefined;
  onSecondaryCTAClick?: () => void;
  primaryCTALabel?: string;
  queryClient: QueryClient;
  secondaryCTALabel?: string;
  closeOnSuccess?: boolean;
};

const TENANT_SELECT_CONTENT = {
  MODAL_TITLE: 'Add tenants',
  PRIMARY_CTA_LABEL: 'Save',
  SECONDARY_CTA_LABEL: '',
} as const;

const TOAST_MESSAGE = {
  TENANTS_ADDED: 'Tenants added successfully',
  TENANT_EDITED: 'Tenant details edited successfully',
  TENANT_REMOVED: 'The tenant has been successfully removed',
} as const;

export const CS_EVENT_TRIGGER_LOCATION = {
  ZRM_PROPERTY_OVERVIEW: 'zrm_property_overview',
  ZRM_POI_ADD_TENANTS_MODAL: 'zrm_poi_add_tenants_modal',
} as const;

const EVENT_IDS = {
  TENANTS_MODULE_SAVE_CLICK: '7909',
} as const;

/**
 * ModalTenantSelect Component
 *
 * A modal wrapper for the TenantSelectFormBody component. This component provides
 * a complete modal experience for adding tenants to a property.
 *
 * @param {TenantSelectProps} props
 * @param {Client} props.client - The HTTP client for API calls
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.listingAlias - The listing alias for the property
 * @param {string} props.modalTitle - The title of the modal header
 * @param {() => void} props.onAddRenterSuccess - Callback when tenants are successfully added
 * @param {Function} props.onAddRenterError - Callback when submission fails
 * @param {() => void} props.onClose - Callback when the modal is closed
 * @param {Function} props.onPrimaryCTAClick - Callback when the primary CTA is clicked
 * @param {() => void} props.onSecondaryCTAClick - Callback for secondary CTA click
 * @param {string} props.primaryCTALabel - Label for the primary CTA button
 * @param {QueryClient} props.queryClient - React Query client for cache management
 * @param {string} props.secondaryCTALabel - Label for the secondary CTA button
 * @param {boolean} props.closeOnSuccess - Whether to close the modal on success
 *
 * @returns {JSX.Element}
 */
const ModalTenantSelect = ({
  client,
  isOpen,
  listingAlias,
  modalTitle = TENANT_SELECT_CONTENT.MODAL_TITLE,
  onAddRenterSuccess,
  onAddRenterError,
  onClose,
  onPrimaryCTAClick = undefined,
  onSecondaryCTAClick = undefined,
  primaryCTALabel = TENANT_SELECT_CONTENT.PRIMARY_CTA_LABEL,
  queryClient,
  secondaryCTALabel = TENANT_SELECT_CONTENT.SECONDARY_CTA_LABEL,
  closeOnSuccess = true,
}: TenantSelectProps): JSX.Element => {
  const [isFormValid, setIsFormValid] = useState(false);
  const tenantSelectFormBodyRef = useRef<TenantSelectFormBodyRef>(null);

  const { enqueueToast } = useToast();

  const handleClose = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  const handleSubmitSuccess = (addedRenters?: Array<RenterInfoRenterAddedDto>) => {
    onPrimaryCTAClick?.(addedRenters);
    onAddRenterSuccess?.();
    if (closeOnSuccess) {
      handleClose(false);
    }
    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.TENANTS_ADDED}</Toast>);
  };

  const handlePrimaryCTAClick = async () => {
    if (tenantSelectFormBodyRef.current) {
      await tenantSelectFormBodyRef.current.addRenters();
    }
  };

  const handleSecondaryCTAClick = () => {
    onSecondaryCTAClick?.();
  };

  /**
   * Invalidate query when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      // Invalidate the likely tenants query to refresh the list
      queryClient.invalidateQueries({
        queryKey: useRentalManagerRenterContactWebApiHandler_getLikelyTenants.getKey({ listingAlias }),
      });
    }
  }, [isOpen, queryClient, listingAlias]);

  return (
    <>
      <Modal
        open={isOpen}
        size={'md'}
        scrollDocument={false}
        header={<Heading level={2}>{modalTitle}</Heading>}
        body={
          <TenantSelectFormBody
            ref={tenantSelectFormBodyRef}
            client={client}
            listingAlias={listingAlias}
            isOpen={isOpen}
            onSubmitSuccess={handleSubmitSuccess}
            onSubmitError={onAddRenterError}
            onValidationChange={setIsFormValid}
          />
        }
        footer={
          <Box css={{ display: 'flex', gap: '100', alignItems: 'center' }}>
            {secondaryCTALabel && (
              <TextButton
                tone="brand"
                onClick={handleSecondaryCTAClick}
                css={{
                  paddingLeft: '200',
                  paddingRight: '200',
                }}
              >
                {secondaryCTALabel}
              </TextButton>
            )}
            <Button
              data-eventid={EVENT_IDS.TENANTS_MODULE_SAVE_CLICK}
              tone={'brand'}
              disabled={!isFormValid}
              onClick={handlePrimaryCTAClick}
            >
              {primaryCTALabel}
            </Button>
          </Box>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalTenantSelect);
